import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Typography, Typography2 } from '@coursera/cds-core';
import { Box } from '@coursera/coursera-ui';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

import type { ProfileNames, UserItemAction } from 'bundles/author-assignment-grading/models/ChangeLogRecord';
import { getAuditTrailByUserItem } from 'bundles/author-assignment-grading/utils/ChangeLogAPIUtils';
import { getCurrentGrade } from 'bundles/author-assignment-grading/utils/gradeCalculationUtils';
import { renderGrade } from 'bundles/author-assignment-grading/utils/gradingDisplayUtils';
import type Item from 'bundles/author-course/models/Item';
import { loadGradebook } from 'bundles/author-gradebook/actions/GradebookActions';
import type SessionItem from 'bundles/author-sessions/models/SessionItem';
import PartnerHelpLink from 'bundles/authoring/common/components/PartnerHelpLink';
import type { ItemGrade } from 'bundles/authoring/common/types/gradebookV2';
import type GroupMember from 'bundles/authoring/groups/models/GroupMember';

import _t from 'i18n!nls/author-assignment-grading';

import 'css!./__styles__/UserChangeLogContainer';

import ChangeLogTable from './ChangeLogTable';
import TeamMemberSelector from './TeamMemberSelector';

export type Props = {
  userId: string;
  currentItem: Item;
  sessionItem?: SessionItem;
  courseBranchId: string;
  grade?: ItemGrade;
  grades?: { [userId: string]: ItemGrade };
  teamMembers?: Array<GroupMember>;
  isAnonymized?: boolean; // whether all learner names on this page should be anonymized
  refetchGrades?: boolean; // whether to refetch grades from gradebook on mount
};

type State = {
  changeLogs: Array<UserItemAction>;
  isLoading: boolean;
  profileNames: ProfileNames;
  currentUserId: string | number;
};

const getCurrentUserId = (props: Props): string | number =>
  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  props.teamMembers && props.teamMembers.length > 0 ? props.teamMembers[0].userId : props.userId;

class UserChangeLogContainer extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof UserChangeLogContainer.contextTypes>;

  static defaultProps = {
    teamMembers: [],
    isAnonymized: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      changeLogs: [],
      isLoading: true,
      profileNames: {},
      currentUserId: getCurrentUserId(props),
    };
  }

  componentDidMount() {
    this.loadAuditTrail(this.props);
    if (this.props.refetchGrades) {
      this.refetchGrades();
    }
  }

  refetchGrades = () => {
    const { sessionItem } = this.props;
    const { executeAction } = this.context;
    if (sessionItem?.sessionId && sessionItem?.groupId) {
      executeAction(loadGradebook, {
        sessionId: sessionItem.sessionId,
        groupId: sessionItem.groupId,
        itemId: sessionItem.id,
      });
    }
  };

  handleMemberSelect = (selectedOption: { label: string; value: string | number }) => {
    this.setState({ currentUserId: selectedOption.value, isLoading: true }, () => this.loadAuditTrail(this.props));
  };

  loadAuditTrail({ currentItem, courseBranchId }: { currentItem: Item; courseBranchId: string }): void {
    const { currentUserId } = this.state;

    getAuditTrailByUserItem(currentUserId, courseBranchId, currentItem.id)
      .then(({ logs = [], profileNames = {} }) => {
        this.setState({
          isLoading: false,
          changeLogs: logs,
          profileNames,
        });
      })
      .catch(() => {
        this.setState({
          isLoading: false,
        });
      }); // TODO: better error state
  }

  renderChangeLogTable(grade?: ItemGrade): React.ReactElement<typeof ChangeLogTable> {
    const { currentItem, sessionItem } = this.props;
    const { changeLogs, profileNames } = this.state;
    return (
      <ChangeLogTable
        grade={grade}
        changeLogs={changeLogs}
        profileNames={profileNames}
        currentItem={currentItem}
        sessionItem={sessionItem}
      />
    );
  }

  renderTeamMemberSelector() {
    const { teamMembers, isAnonymized } = this.props;

    if (!teamMembers || teamMembers.length === 0) {
      return null;
    }

    const { currentUserId } = this.state;
    const currentUser = teamMembers.find((member) => member.userId === currentUserId);

    if (!currentUser) {
      return null;
    }

    const selectorOptions = teamMembers.map((member, idx) => {
      const { userId } = member;

      return {
        label: member.getDisplayName(isAnonymized, idx + 1),
        value: userId,
      };
    });

    return (
      <div className="user-selector m-b-2 horizontal-box align-items-vertical-center">
        <span className="font-sm">{_t('Showing change log for team member: ')}</span>
        <TeamMemberSelector
          options={selectorOptions}
          currentUserId={currentUserId}
          onUserChange={this.handleMemberSelect}
        />
      </div>
    );
  }

  render() {
    const { grade, grades } = this.props;
    const { isLoading, currentUserId } = this.state;
    const currentUserGrade = grades ? grades[currentUserId] : grade;

    return (
      <div className="rc-UserChangeLogContainer">
        <div className="header">
          <Typography variant="h1semibold" component="h2" className="change-log-header m-b-1">
            {_t('Change Log')}
          </Typography>
          <Typography2 component="p" variant="bodyPrimary" className="cui-p-margin">
            {_t(
              `Change Log is a reverse chronological list of grade-related changes made on a per-learner and per-assignment basis for this submission.
              Grade-related changes may take several minutes to appear in the change log table below.`
            )}
            &nbsp;
            <PartnerHelpLink articleId="360023831732" />
          </Typography2>
        </div>
        <div className="content">
          {this.renderTeamMemberSelector()}
          <Box justifyContent="start" alignItems="center" rootClassName="current-grade-container m-b-1">
            <Typography2 component="p" variant="bodyPrimary" style={{ marginRight: 24, marginBottom: 0 }}>
              {_t('Current assignment grade')}: &nbsp;
              <strong data-testid="current-grade">{renderGrade(getCurrentGrade(currentUserGrade))}</strong>
            </Typography2>
            <Typography2 component="p" variant="bodyPrimary" style={{ marginRight: 24, marginBottom: 0 }}>
              {_t('Grade Visibility')}: &nbsp;
              <strong data-testid="grade-visibility">
                {typeof currentUserGrade === 'undefined' && '--'}
                {typeof currentUserGrade !== 'undefined' &&
                  (currentUserGrade.isHidden ? _t('Hidden from learner') : _t('Shown to learner'))}
              </strong>
            </Typography2>
          </Box>
          {isLoading && (
            <Box justifyContent="center" alignItems="center" rootClassName="m-t-3">
              <SvgLoaderSignal size={54} />
            </Box>
          )}
          {!isLoading && this.renderChangeLogTable(currentUserGrade)}
        </div>
      </div>
    );
  }
}

export default UserChangeLogContainer;
